import {
  GetConsultant_references,
  GetConsultant_allReferences,
  TodoPriority,
  CriminalRecordType,
  TodoInput
} from "@graphql";
import { action, observable, computed } from "mobx";
import { FormErrors, FormStore } from "@modules/formx";
import locale from "@bonliva-crm/locale";
import { CreateTicketData } from "./CreateTicketData";
import yup from "yup";
import moment from "moment";
import { regexps } from "@bonliva-crm/common";
import { TodoTags } from "@bonliva-crm/common";
import { ConsultantSITHSErrand } from "../utils";

type ConsultantReferencesData = GetConsultant_allReferences.Return["consultantById"];

type References = any; // deleted in CRMDEV-3435

export class TicketCreateStore {
  form: FormStore<CreateTicketData>;
  @observable isSubmitting = false;
  @observable tags = [];
  @observable references: References = [];
  @observable referencesOpt: LabelValue[] = [];
  @observable selectedReference: string;
  @observable isSITHSQualityItem = false;
  @observable isHLRLikeQualityItem = false;
  @observable isCriminalRecordNational = false;

  constructor(
    initialValues: CreateTicketData | null,
    private dueDateRequired: boolean,
    private descriptionRequired: boolean,
    private criminalRecordTypeRequired: boolean,
    private onCreated: ((input: TodoInput, formValues: CreateTicketData) => void) | null,
    private referenceRequired: boolean,
    private consultantId?: string | null,
    private customValidation?: (
      form: FormStore<CreateTicketData>
    ) => FormErrors<CreateTicketData>,
    itemName?: string,
    isNewConsultantProfile?: boolean
  ) {
    this.isSITHSQualityItem = itemName === "HSAID/SITHS";
    this.isHLRLikeQualityItem = [
      "HLR",
      "S-HLR Barn",
      "S-HLR Vuxen",
      "A-HLR Vuxen",
      "A-HLR Barn"
    ].includes(itemName);
    this.isCriminalRecordNational = itemName === "Belastningsregister Nationella";

    if (this.isCriminalRecordNational) {
      initialValues.criminalRecordType = CriminalRecordType.HVB;
    }

    this.form = new FormStore<CreateTicketData>({
      initialValues: initialValues || CreateTicketData.Empty(),
      initialDirty: !!initialValues,
      onSubmit: () => Promise.resolve(),
      validate: (formStore) =>
        this.customValidation
          ? this.customValidation(formStore)
          : this.validate(formStore.values)
    });

    this.tags = Object.values(TodoTags).map((x) => ({ label: x, value: x }));

    if (referenceRequired) {
      if (isNewConsultantProfile) {
        GetConsultant_allReferences.func(
          { id: consultantId },
          { fetchPolicy: "network-only" }
        ).then(({ data }) => {
          this.references = this.getConsultantReferences(data.consultantById);
          this.referencesOpt = this.references.toLabelValue(
            (v) => `${v.Name} - ${v.Position}`,
            (v) => v._id
          );

          if (
            initialValues.reference._id &&
            this.referencesOpt.some((x) => x.value === initialValues.reference._id)
          ) {
            this.selectedReference = initialValues.reference._id;
          }
        });
      } else {
        GetConsultant_references.func(
          { id: consultantId },
          { fetchPolicy: "network-only" }
        ).then(({ data }) => {
          // this.references = data.consultantById.References;
          this.references = [];
          this.referencesOpt = this.references.toLabelValue(
            (v) => `${v.Name} - ${v.Position}`,
            (v) => v._id
          );
        });
      }
    }
  }

  @computed get SITHSQualityItemInfo() {
    if (!this.isSITHSQualityItem) {
      return null;
    }

    const { dueDate } = this.form.values;

    if (dueDate && !this.isDueDateValidForSITHS(dueDate)) {
      return {
        text: locale.tickets.deadlineInfo,
        error: true
      };
    }

    return {
      text: locale.tickets.consultantOrderInfo
    };
  }

  isDueDateValidForSITHS(date: Date): boolean {
    const today = moment().startOf("day");
    const dueDateMoment = moment(date).startOf("day");

    const diffDays = dueDateMoment.diff(today, "days");

    return diffDays > 10;
  }

  validate = (values: CreateTicketData): FormErrors<CreateTicketData> => {
    const errors: FormErrors<CreateTicketData> = {};

    if (this.dueDateRequired && !values.dueDate) {
      errors.dueDate = locale.tickets.dueDate;
    }

    if (this.descriptionRequired && !values.description?.trim()) {
      errors.description = locale.tickets.description;
    }

    if (this.criminalRecordTypeRequired && !values.criminalRecordType?.trim()) {
      errors.criminalRecordType = locale.tickets.criminalRecordType;
    }

    if (this.isSITHSQualityItem) {
      if (
        !values.deliveryPlace?.trim() &&
        values.errand !== ConsultantSITHSErrand.ExitCard
      ) {
        errors.deliveryPlace = locale.tickets.deliveryPlace;
      }

      if (!values.workInStockholm) {
        errors.workInStockholm = locale.tickets.workInStockholm;
      }

      if (values.dueDate && !this.isDueDateValidForSITHS(values.dueDate)) {
        errors.dueDate = locale.tickets.dueDate;
      }
    }

    if (this.referenceRequired) {
      const schema = yup.object().shape(
        {
          name: yup
            .string()
            .nullable(true)
            .required(locale.fieldIsMandatory(locale.Name)),
          email: yup
            .string()
            .email(locale.FillCorrect(locale.Email))
            .nullable(true)
            .when("phone", {
              is: (v) => !v,
              then: (s) => s.required(locale.fieldIsMandatory(locale.Email)),
              otherwise: (s) => s
            }),
          phone: yup
            .string()
            .matches(regexps.phoneNumber, locale.PhoneNumberMissing)
            .nullable(true)
            .when("email", {
              is: (v) => !v,
              then: (s) => s.required(locale.fieldIsMandatory(locale.PhoneNumberMissing)),
              otherwise: (s) => yup.string()
            }),
          relation: yup
            .string()
            .nullable(true)
            .required(locale.fieldIsMandatory(locale.ReferenceWorkplace)),
          position: yup
            .string()
            .nullable(true)
            .required(locale.fieldIsMandatory(locale.ReferencePosition))
        },
        [["phone", "email"]]
      );
      schema.validate(values.reference, { abortEarly: false }).catch((err) => {
        errors.reference = {};
        // eslint-disable-next-line no-unsafe-optional-chaining
        for (const x of err?.inner) {
          errors.reference[x.path] = x.message;
          this.form.setError(`reference.${x.path}`, x.message);
        }
      });
    }
    return errors;
  };

  @action submit = async () => {
    if (!this.form.canSubmit) {
      return;
    }

    const { values } = this.form;

    this.isSubmitting = true;

    try {
      const input: TodoInput = {
        Text: values.title,
        Description: values.description,
        Due: values.dueDate,
        Reminder: !!values.dueDate,
        Tags: values.tags,
        Priority: values.priority,
        DueDateText: values.dueDateText,
        CriminalRecordType: values.criminalRecordType,
        Reference: values.reference,
        Errand: values.errand as any,
        DeliveryPlace: values.deliveryPlace,
        WorkInStockholm: values.workInStockholm,
        EducationType: values.educationType as any,
        ChargedProjects: values.chargedProjects,
        PerfPlace: values.perfPlace
      };

      this.onCreated?.(input, this.form.values);
    } catch (e) {
      console.log("error", e);
    } finally {
      this.form.reset(CreateTicketData.Empty());
      this.isSubmitting = false;
    }
  };

  @action setAssignedTeam = (value: string) => {
    this.form.values.assignedTeam = value;
    this.form.values.assignedUser = null;
    this.form.validate();
    this.form.changed();
  };

  @action setPriority = (value: TodoPriority) => {
    this.form.values.priority = value;
    this.form.validate();
    this.form.changed();
  };

  @action setTag = (value: string[]) => {
    this.form.values.tags = value;
    this.form.validate();
    this.form.changed();
  };

  @action handleChangeReferences = (v) => {
    const item = this.references.find((x) => x._id === v);
    this.selectedReference = v;
    if (item) {
      this.form.setValue("reference", {
        name: item.Name,
        position: item.Position,
        phone: item.Phone,
        relation: item.Relation,
        email: item.Email
      });
    }
  };

  get priorityOptions() {
    return [
      { label: "Ingen prioritet", value: null },
      { label: "Låg prioritet", value: "Low" },
      { label: "Hög prioritet", value: "High" }
    ];
  }

  get priorityHelperText() {
    if (this.form.values.priority === "High") {
      return this.form.values.priorityHelperText;
    }

    return "";
  }

  private getConsultantReferences(consultant: ConsultantReferencesData) {
    // const consulantReferences: References = [];
    //
    // if (consultant.References?.length) {
    //   consulantReferences.push(...consultant.References);
    // }
    //
    // if (consultant.EvaluationsByCustomer?.length) {
    //   for (const item of consultant.EvaluationsByCustomer) {
    //     consulantReferences.push({ ...item, Relation: item.WorkPlace });
    //   }
    // }
    //
    // if (consultant.EvaluationsToThreeMonthsByCustomer?.length) {
    //   for (const item of consultant.EvaluationsByCustomer) {
    //     consulantReferences.push({ ...item, Relation: item.WorkPlace });
    //   }
    // }
    //
    // return consulantReferences.filter((x) => x.Name);

    return [];
  }
}
